import { Box, Paper, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ColumnVisibilityButton from "../../../components/button/ColumnVisibilityButton";
import RefreshButton from "../../../components/button/RefreshButton";
import { TableHeaderContainer } from "../../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../../components/container/TableTitleContainer";
import ColumnMenu from "../../../components/context-menu/ColumnMenu";
import AgGrid from "../../../components/grid/AgGrid";
import { defaultColDef, leftAlignColDef, rightAlignColDef } from "../../../components/grid/columnDefinitions/columnDefinitions";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import { computeBciLayerRows, computeBciMetrics, truncateTo2Decimals } from "./shearingLayers";

// Building-only. Building Circularity Indicator table — same AgGrid + toolbar shell as SCI
// Calculation. No Product Name column here, so rows collapse to one per Shearing Layer (6
// rows: Site, Structure, Skin, Services, Space Plan, Stuff) instead of the 15
// WBLCA-subcategory rows SCI Calculation shows. SCI (theoretical)/(practical) per layer are
// exactly the SCI Calculation tab's per-layer values (via the shared
// computeShearingLayerRows in shearingLayers.js) — not recomputed independently.
// Importance Weight is a fixed value per layer.
// BCI (theoretical)/(practical) are single whole-building values — computed "layer wise",
// i.e. summed once per Shearing Layer:
//   BCI (theoretical) = Σ(SCI theoretical × Weight) / Σ(Weight), across the 6 layers
//   BCI (practical)   = Σ(SCI practical × Weight) / Σ(Weight), across the 6 layers
// The denominator Σ(Weight) is always the FULL sum of all 6 layers' weights, even ones
// with no SCI value yet (e.g. Site Layer) — those layers just contribute 0 to the
// numerator, they still count against the total. Shown once in the table footer (not as
// grid columns), same pattern as ProjectMCICalculationTable's Product MCI footer values.
const BuildingBCICalculationTable = ({ combined_data, processes = [], activeTab, tabValue }) => {
  const { columnVisibilities } = useContextProvider();
  const { t } = useTranslation();
  const colVisibility_key = "BCI_calculation_table_column_visibility";
  const gridHeaderContainerRef = useRef(null);
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);

  // One row per unique Shearing Layer (no Product Name column, so the 15 individual
  // subcategory rows collapse down to their 6 parent layers). computeBciLayerRows /
  // computeBciMetrics are shared with the Building Circularity metric cards, which show the
  // same BCI Theoretical/Practical value computed here.
  const rows = useMemo(() => computeBciLayerRows(combined_data, processes), [combined_data, processes]);
  const bciMetrics = useMemo(() => computeBciMetrics(combined_data, processes), [combined_data, processes]);

  useEffect(() => {
    if (activeTab && tabValue && activeTab !== tabValue) {
      setColumnMenuVisible(false);
    }
  }, [activeTab, tabValue]);

  const colDefs = useMemo(() => {
    const isHidden = (field) => {
      if (field === "si_no") return false;
      return !columnVisibilities[colVisibility_key]?.includes(field);
    };
    const blankIfMissing = (params) => (params.value === null || params.value === undefined ? "" : Number(params.value).toFixed(2));
    return [
      {
        headerName: t("SI No."),
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 80,
        minWidth: 60,
        headerClass: "ag-center-aligned-header",
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
        suppressMenu: true,
        sortable: false,
        field: "si_no"
      },
      { headerName: t("Shearing Layer"), field: "shearingLayer", ...leftAlignColDef, flex: 1, minWidth: 180 },
      { headerName: t("Importance Weight"), field: "importanceWeight", ...rightAlignColDef, minWidth: 160, valueFormatter: blankIfMissing },
      { headerName: t("SCI (theoretical)"), field: "sciTheoretical", ...rightAlignColDef, minWidth: 160, valueFormatter: blankIfMissing },
      { headerName: t("SCI (practical)"), field: "sciPractical", ...rightAlignColDef, minWidth: 150, valueFormatter: blankIfMissing },
    ].map((col) => ({ ...defaultColDef, ...col, hide: isHidden(col.field) }));
  }, [columnVisibilities, colVisibility_key, t]);
  const defaultCols = colDefs.map((col) => col.field);

  return (
    <Paper sx={{ border: 1, borderColor: "#e0e0e0", borderRadius: 4, overflow: "hidden" }}>
      <TableHeaderContainer ref={gridHeaderContainerRef}>
        <TableTitleContainer>
          <Typography variant="Table Title">{t("BCI Calculation")}</Typography>
        </TableTitleContainer>
        <TableHeaderToolsContainer>
          <ColumnVisibilityButton onClick={() => setColumnMenuVisible((p) => !p)} />
          <RefreshButton />
        </TableHeaderToolsContainer>
      </TableHeaderContainer>
      <Box sx={{ height: "auto" }}>
        <AgGrid
          columnDefs={colDefs}
          rowData={rows}
          headerCheckbox={false}
          domLayout="autoHeight"
          footerLeftContent={
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                  {t("BCI Theoretical:")}
                </Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "text.primary" }}>{truncateTo2Decimals(bciMetrics.bciTheoretical)}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                  {t("BCI Practical:")}
                </Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "text.primary" }}>{truncateTo2Decimals(bciMetrics.bciPractical)}</Typography>
              </Box>
            </Box>
          }
        />
      </Box>
      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={colDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
      />
    </Paper>
  );
};
export default BuildingBCICalculationTable;
